<template lang="pug">
  div
    v-row(no-gutters, align="start")
      v-col.shrink
        yz-btn.mr-4(outlined, size="lg" icon="mdi-arrow-left" background-color="white" @click="onGoBack")

      v-col
        .d-flex.align-center
          .yz-heading-5.mr-2 {{ $t('panel.panel_claims') }} {{ claimData.rid }}
          .yz-medium-body-lg.mr-3 {{ $t('panel.claim_for') }} {{ claimData.amount | displayPrice($currency) }}
          claim-status-badge(:status="claimData.status")

        .d-flex.align-center
          .yz-regular-body-sm {{ $t('panel.submitted_on') }} {{ claimData.date | humanizeDateLong }}
          yz-divider.mx-3(vertical, color="yz-neutral-400")
          .yz-regular-body-sm
            span.mr-2 {{ $t('panel.updated_by') }}
            div(v-if="claimData.updatedBy === 'N/A'" style="display: inline;")
              span.text-neutral-500 N/A
            div(v-else style="display: inline;")
              yz-avatar.mr-1(size="14")
                yz-img(v-if="claimData.updatedBy.profile_image && claimData.updatedBy.profile_image.file" :src="`https://img.yezza.io/50x50/${claimData.updatedBy.profile_image.path}`")
                yz-icon(v-else size="16px" color="yz-neutral-500") $yzi-user-edit
              span {{ claimData.updatedBy.name }}

      v-col(cols="auto")
        .d-flex.align-center.gap-3
          yz-btn(:label="$t('copy_link')" size="lg" outlined, prepend-icon="$yzi-link-03" icon-color="yz-neutral-500" background-color="white" @click="onCopyLink")
          yz-btn(:label="$t('panel.download_pdf')" size="lg" outlined, prepend-icon="$yzi-download-01" icon-color="yz-neutral-500" background-color="white" @click="onDownloadPdf")
          yz-btn(:label="$t('panel.send_claim')" size="lg" outlined, prepend-icon="$yzi-line-send-01" icon-color="yz-neutral-500" background-color="white" @click="onSendClaim")
          yz-btn(:label="$t('panel.edit_claim')" size="lg" outlined, background-color="white" @click="onEditClaim")

    // Edit Claim Dialog
    create-bulk-claim-dialog(v-if="showEditDialog" v-model="showEditDialog" :parent-claim="parentClaim" @cancel="onEditCancel" @updated="onEditSave")

    // Send Claim Dialog
    send-claim-dialog(v-if="showSendClaimDialog" v-model="showSendClaimDialog" :claim-id="parentClaim.id")
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { generatePanelClaimLink } from '@/utils'
import ClaimStatusBadge from '@/components/clinic/panel/claims/ClaimStatusBadge.vue'
import CreateBulkClaimDialog from '@/components/clinic/panel/dialogs/bulk/CreateBulkClaimDialog.vue'
import SendClaimDialog from '@/components/clinic/panel/dialogs/SendClaimDialog.vue'

export default {
  components: { ClaimStatusBadge, CreateBulkClaimDialog, SendClaimDialog },
  props: {
    parentClaim: {
      type: Object,
      required: true
    },
    parentInvoices: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      showEditDialog: false,
      showSendClaimDialog: false,
      claimData: {
        rid: '',
        amount: 0,
        status: '',
        date: '',
        updatedBy: ''
      }
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({
      company: 'getCompany'
    })
  },
  mounted() {
    this.mapClaimData()
  },
  watch: {
    parentClaim: {
      handler() {
        this.mapClaimData()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    mapClaimData() {
      if (!this.parentClaim) return

      this.claimData.rid = this.parentClaim.rid || ''
      this.claimData.amount = this.parentClaim.submitted_amount || 0
      this.claimData.status = this.parentClaim.status || ''
      this.claimData.date = this.getStatusDate(this.parentClaim) || ''
      this.claimData.updatedBy = this.parentClaim.updated_by || 'N/A'
    },
    getStatusDate(claim) {
      const statusDateMap = {
        submitted: claim.submitted_date,
        approved: claim.approved_date,
        rejected: claim.rejected_date,
        received: claim.received_date
      }
      return statusDateMap[claim.status] || claim.submitted_date
    },
    onGoBack() {
      this.$emit('go-back')
    },
    async onCopyLink() {
      try {
        const link = generatePanelClaimLink(this.company.slug, this.parentClaim.pid)
        if (!link) {
          await this.showMessage(['error', this.$t('panel.unable_to_generate_link')])
          return
        }

        if (navigator.clipboard) {
          await navigator.clipboard.writeText(link)
        } else {
          // Fallback for browsers that don't support clipboard API
          const textArea = document.createElement('textarea')
          textArea.value = link
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
        }
        await this.showMessage(['success', this.$t('panel.link_copied')])
      } catch (err) {
        console.error('Failed to copy link: ', err)
        await this.showMessage(['error', this.$t('panel.failed_copy_link')])
      }
    },
    async onDownloadPdf() {
      try {
        // Generate OTK for security
        const { data: otkData } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        const key = otkData.key

        // Determine base URL
        let baseUrl = process.env.VUE_APP_BASE_URL
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          baseUrl = 'https://api-test.yezza.io'
        } else if (process.env.VUE_APP_ENV === 'prod') {
          baseUrl = 'https://export.yezza.io'
        }

        // Construct URL and open PDF in new tab
        const url = `${baseUrl}/panels/${this.company.slug}/claims/${this.parentClaim.pid}/preview_document?key=${key}`
        window.open(url, '_blank')

        await this.showMessage(['success', this.$t('panel.download_started')])
      } catch (error) {
        console.error('Error downloading claim PDF:', error)
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    onSendClaim() {
      this.showSendClaimDialog = true
    },
    onEditClaim() {
      this.showEditDialog = true
    },
    onEditCancel() {
      this.showEditDialog = false
    },
    onEditSave() {
      this.showEditDialog = false
      this.$emit('updated')
    }
  }
}
</script>

<style scoped></style>
